import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Button,
  cardStyle,
  chipStyle,
  dangerButtonStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n, type I18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import {
  LOYALTY_CONFIG_DOC_ID,
  achievementsRepoFor,
  loyaltyConfigRepoFor,
} from '../lib/repositories/loyalty-repo.js';
import { logger } from '../lib/logger.js';
import {
  AUTHORABLE_ACHIEVEMENT_CRITERIA,
  achievementCriteriaNeedsThreshold,
  buildAchievementDefinition,
  InvalidAchievementDefinitionError,
  money,
  type Achievement,
  type AuthorableAchievementCriteriaKind,
  type LoyaltyConfig,
} from '@kilat/shared';

/**
 * /loyalty/achievements — operator-side CRUD for milestone BADGES (the lifetime
 * sibling of the time-bounded /loyalty/challenges page).
 *
 * An operator authors a badge ("Loyal — 10 washes") with an icon, a name +
 * description, an auto-evaluable milestone (first wash / N washes / RM spent /
 * weekly streak), and an active flag. `onTicketCollected` reads the tenant's
 * ACTIVE definitions post-transaction and writes an earned doc when a customer
 * crosses the threshold.
 *
 * Writes go through the rules-guarded `achievementsRepoFor` (owner/manager, same
 * `loyaltyProgram/{doc=**}` rule as tiers/challenges/vouchers). Each def is built
 * + validated by the pure `buildAchievementDefinition`, which FORCES
 * `reward: { kind: 'none' }` — badges are the dopamine, never points/vouchers
 * (that would make the surface money-adjacent + out of scope).
 *
 * The engine only reads a badge while `enabledFeatures.achievements` is on AND it
 * is active — so this page surfaces the master toggle's state, with explicit
 * loading / empty / error / saving states (no dead-ends).
 */
export function LoyaltyAchievementsPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const achievementsSubscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = achievementsRepoFor(tenantId);
    return (cb: (vs: Achievement[]) => void) => repo.listSnapshot(cb, () => cb([]));
  }, [tenantId]);
  const { data: achievements, loading } = useTrackedSnapshot<Achievement[]>(
    ['achievements', tenantId],
    achievementsSubscribe,
  );

  // Master toggle + streaks flag — read-only surfaced so the operator knows
  // whether the engine reads these badges (edited on /loyalty/settings).
  const configSub = useMemo(() => {
    if (!tenantId) return null;
    const repo = loyaltyConfigRepoFor(tenantId);
    return (cb: (v: LoyaltyConfig | null) => void) => repo.snapshot(LOYALTY_CONFIG_DOC_ID, cb);
  }, [tenantId]);
  const { data: config } = useTrackedSnapshot<LoyaltyConfig | null>(
    ['achievements-config', tenantId],
    configSub,
  );

  const [editingId, setEditingId] = useState<string | null>(null);

  if (!tenantId) return <Screen body={t.t('dashboard.achievements.signInPrompt')} />;
  if (loading) return <Screen body={t.t('dashboard.achievements.loading')} />;

  const list = (achievements ?? []).slice().sort((a, b) => a.id.localeCompare(b.id));
  const editing = editingId ? (list.find((a) => a.id === editingId) ?? null) : null;
  const enabled = config?.enabledFeatures.achievements === true;
  const streaksOn = config?.enabledFeatures.streaks === true;

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.achievements.title')}
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 0,
            }}
          >
            {t.t(
              list.length === 1
                ? 'dashboard.achievements.countOne'
                : 'dashboard.achievements.countOther',
              { count: list.length },
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Link to="/loyalty/settings" style={ghostLinkStyle}>
            {t.t('dashboard.achievements.backToLoyalty')}
          </Link>
          <Link to="/queue" style={ghostLinkStyle}>
            {t.t('dashboard.achievements.backToQueue')}
          </Link>
        </div>
      </header>

      {config && !enabled ? (
        <div style={disabledBannerStyle} role="status">
          <p style={{ margin: 0, fontWeight: 500 }}>
            {t.t('dashboard.achievements.disabledTitle')}
          </p>
          <p style={{ ...helpTextStyle, marginTop: 'var(--space-1)' }}>
            {t.t('dashboard.achievements.disabledBody')}{' '}
            <Link to="/loyalty/settings" style={{ color: 'var(--color-primary)' }}>
              {t.t('dashboard.achievements.disabledLink')}
            </Link>
          </p>
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-6)',
          alignItems: 'flex-start',
        }}
      >
        <section style={{ ...cardStyle, flex: '2 1 320px', minWidth: 0 }}>
          <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.achievements.listHeading')}
          </h2>
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
            {list.map((achievement) => (
              <AchievementRow
                key={achievement.id}
                tenantId={tenantId}
                achievement={achievement}
                isEditing={achievement.id === editingId}
                onEdit={() => setEditingId(achievement.id)}
              />
            ))}
            {list.length === 0 ? (
              <p
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  textAlign: 'center',
                  margin: `var(--space-6) 0`,
                }}
              >
                {t.t('dashboard.achievements.empty')}
              </p>
            ) : null}
          </div>
        </section>

        <section style={{ ...cardStyle, flex: '1 1 320px', minWidth: 0, maxWidth: 460 }}>
          <AchievementForm
            tenantId={tenantId}
            editing={editing}
            streaksOn={streaksOn}
            onDone={() => setEditingId(null)}
            onCancelEdit={() => setEditingId(null)}
          />
        </section>
      </div>
    </main>
  );
}

// ── achievement row ──────────────────────────────────────────────────────

function AchievementRow({
  tenantId,
  achievement,
  isEditing,
  onEdit,
}: {
  readonly tenantId: string;
  readonly achievement: Achievement;
  readonly isEditing: boolean;
  readonly onEdit: () => void;
}) {
  const t = useI18n();
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const name = achievement.name[t.locale] ?? achievement.name['en'] ?? achievement.id;

  async function toggleActive() {
    setBusy(true);
    try {
      await achievementsRepoFor(tenantId).update(achievement.id, { active: !achievement.active });
    } catch (e) {
      logger.error('achievement toggle failed', { error: String(e), id: achievement.id });
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    try {
      await achievementsRepoFor(tenantId).delete(achievement.id);
      logger.info('achievement deleted', { tenantId, id: achievement.id });
      // Row disappears when the snapshot updates.
    } catch (e) {
      logger.error('achievement delete failed', { error: String(e), id: achievement.id });
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: isEditing ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
        background: isEditing ? 'rgba(13,122,79,0.04)' : 'var(--color-surface)',
        opacity: achievement.active ? 1 : 0.82,
      }}
    >
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontWeight: 500,
            overflowWrap: 'anywhere',
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'center',
          }}
        >
          <span aria-hidden>{achievement.icon}</span>
          {name}
        </p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {describeCriteria(achievement.criteria, t)}
        </p>
      </div>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}
      >
        <span
          style={{
            background: achievement.active ? 'rgba(13,122,79,0.10)' : 'var(--color-border)',
            color: achievement.active ? 'var(--color-primary)' : 'var(--color-on-surface-muted)',
            padding: '4px 10px',
            borderRadius: 'var(--radius-pill)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {achievement.active
            ? t.t('dashboard.achievements.row.active')
            : t.t('dashboard.achievements.row.paused')}
        </span>
        {confirmingDelete ? (
          <>
            <button
              type="button"
              onClick={() => void doDelete()}
              disabled={busy}
              style={dangerButtonStyle}
            >
              {busy
                ? t.t('dashboard.achievements.row.deleting')
                : t.t('dashboard.achievements.row.confirmDelete')}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              style={miniGhostButtonStyle}
            >
              {t.t('dashboard.achievements.row.cancel')}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => void toggleActive()}
              disabled={busy}
              style={miniGhostButtonStyle}
            >
              {achievement.active
                ? t.t('dashboard.achievements.row.pause')
                : t.t('dashboard.achievements.row.resume')}
            </button>
            <button
              type="button"
              onClick={onEdit}
              disabled={isEditing}
              style={miniGhostButtonStyle}
            >
              {isEditing
                ? t.t('dashboard.achievements.row.editing')
                : t.t('dashboard.achievements.row.edit')}
            </button>
            <Button variant="danger" onClick={() => setConfirmingDelete(true)}>
              {t.t('dashboard.achievements.row.delete')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ── achievement form (create + edit) ────────────────────────────────────────

interface FormState {
  name: string;
  icon: string;
  description: string;
  criteriaKind: AuthorableAchievementCriteriaKind;
  /** Washes / weeks, or RINGGIT (major units) when criteriaKind is spent_over. */
  threshold: string;
  active: boolean;
}

function AchievementForm({
  tenantId,
  editing,
  streaksOn,
  onDone,
  onCancelEdit,
}: {
  readonly tenantId: string;
  readonly editing: Achievement | null;
  readonly streaksOn: boolean;
  readonly onDone: () => void;
  readonly onCancelEdit: () => void;
}) {
  const t = useI18n();
  const initial = useMemo(() => formFromAchievement(editing), [editing]);
  const [state, setState] = useState<FormState>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  // Reset every field when the edited badge changes (toggle Edit / Cancel).
  useEffect(() => {
    setState(initial);
    setError(null);
    setSavedNotice(null);
  }, [editing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function patch(p: Partial<FormState>) {
    setState((s) => ({ ...s, ...p }));
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSavedNotice(null);

    const trimmedName = state.name.trim();
    if (!trimmedName) {
      setError(t.t('dashboard.achievements.form.errName'));
      return;
    }

    const needsThreshold = achievementCriteriaNeedsThreshold(state.criteriaKind);
    let threshold: number | undefined;
    if (needsThreshold) {
      const raw = Number(state.threshold);
      if (!Number.isFinite(raw) || raw <= 0) {
        setError(t.t('dashboard.achievements.form.errThreshold'));
        return;
      }
      // spent_over stores INTEGER SEN; the input is in RM (major units).
      threshold =
        state.criteriaKind === 'spent_over'
          ? Math.max(1, Math.round(raw * 100))
          : Math.max(1, Math.round(raw));
    }

    const icon = state.icon.trim() || '🏅';
    const descriptionEn = state.description.trim();

    setSubmitting(true);
    try {
      const id = editing ? editing.id : `ach-${Math.random().toString(36).slice(2, 8)}`;
      const def = buildAchievementDefinition({
        id,
        icon,
        // Author English; PRESERVE any existing ms/zh/ta translations on edit.
        name: { ...(editing?.name ?? {}), en: trimmedName },
        description: descriptionEn
          ? { ...(editing?.description ?? {}), en: descriptionEn }
          : editing?.description && Object.keys(editing.description).length > 0
            ? editing.description
            : { en: trimmedName },
        criteriaKind: state.criteriaKind,
        threshold,
        active: state.active,
      });

      await achievementsRepoFor(tenantId).set(id, def);
      if (editing) {
        logger.info('achievement updated', { tenantId, id });
        setSavedNotice(t.t('dashboard.achievements.form.updated', { name: trimmedName }));
        onDone();
      } else {
        logger.info('achievement created', { tenantId, id });
        setSavedNotice(t.t('dashboard.achievements.form.created', { name: trimmedName }));
        setState(formFromAchievement(null));
      }
    } catch (e2) {
      logger.error('achievement save failed', { error: String(e2), editing: editing?.id });
      if (e2 instanceof InvalidAchievementDefinitionError && e2.code === 'invalid_threshold') {
        setError(t.t('dashboard.achievements.form.errThreshold'));
      } else if (e2 instanceof InvalidAchievementDefinitionError && e2.code === 'invalid_name') {
        setError(t.t('dashboard.achievements.form.errName'));
      } else {
        setError(t.t('dashboard.achievements.form.errSave'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const isEditMode = editing !== null;
  const showStreakWarning = state.criteriaKind === 'streak_n_weeks' && !streaksOn;

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <header>
        <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
          {isEditMode
            ? t.t('dashboard.achievements.form.editTitle', {
                name: editing.name['en'] ?? editing.id,
              })
            : t.t('dashboard.achievements.form.createTitle')}
        </h2>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 'var(--space-1) 0 0 0',
          }}
        >
          {isEditMode
            ? t.t('dashboard.achievements.form.editSubtitle')
            : t.t('dashboard.achievements.form.createSubtitle')}
        </p>
      </header>

      {savedNotice ? (
        <p
          role="status"
          style={{
            margin: 0,
            color: 'var(--color-primary)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {savedNotice}
        </p>
      ) : null}

      <label style={fieldGrid}>
        <span style={fieldLabel}>{t.t('dashboard.achievements.form.nameLabel')}</span>
        <input
          required
          value={state.name}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder={t.t('dashboard.achievements.form.namePlaceholder')}
          style={inputStyle}
        />
      </label>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 88px) minmax(0, 1fr)',
          gap: 'var(--space-3)',
        }}
      >
        <label style={fieldGrid}>
          <span style={fieldLabel}>{t.t('dashboard.achievements.form.iconLabel')}</span>
          <input
            value={state.icon}
            onChange={(e) => patch({ icon: e.target.value })}
            placeholder="🏅"
            style={{ ...inputStyle, textAlign: 'center' }}
          />
        </label>
        <label style={fieldGrid}>
          <span style={fieldLabel}>{t.t('dashboard.achievements.form.descLabel')}</span>
          <input
            value={state.description}
            onChange={(e) => patch({ description: e.target.value })}
            placeholder={t.t('dashboard.achievements.form.descPlaceholder')}
            style={inputStyle}
          />
        </label>
      </div>

      {/* Milestone */}
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={fieldLabel}>{t.t('dashboard.achievements.form.criteriaLegend')}</legend>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-2)',
            flexWrap: 'wrap',
          }}
        >
          {AUTHORABLE_ACHIEVEMENT_CRITERIA.map((kind) => (
            <button
              key={kind}
              type="button"
              aria-pressed={state.criteriaKind === kind}
              onClick={() => patch({ criteriaKind: kind })}
              style={chipStyle(state.criteriaKind === kind)}
            >
              {t.t(`dashboard.achievements.form.criteria.${kind}`)}
            </button>
          ))}
        </div>

        {achievementCriteriaNeedsThreshold(state.criteriaKind) ? (
          <label style={{ ...fieldGrid, marginTop: 'var(--space-3)' }}>
            <span style={fieldLabel}>
              {t.t(`dashboard.achievements.form.thresholdLabel.${state.criteriaKind}`)}
            </span>
            <input
              type="number"
              min={1}
              step={state.criteriaKind === 'spent_over' ? 'any' : 1}
              value={state.threshold}
              onChange={(e) => patch({ threshold: e.target.value })}
              style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
            />
          </label>
        ) : (
          <p style={{ ...helpTextStyle, marginTop: 'var(--space-2)' }}>
            {t.t('dashboard.achievements.form.firstWashHint')}
          </p>
        )}

        {showStreakWarning ? (
          <p
            role="status"
            style={{ ...helpTextStyle, marginTop: 'var(--space-2)', color: 'var(--color-accent)' }}
          >
            {t.t('dashboard.achievements.form.streakOffWarning')}
          </p>
        ) : null}
      </fieldset>

      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={state.active}
          onChange={(e) => patch({ active: e.target.checked })}
          style={{ width: 18, height: 18, cursor: 'pointer' }}
        />
        <span style={fieldLabel}>
          {state.active
            ? t.t('dashboard.achievements.form.activeOn')
            : t.t('dashboard.achievements.form.activeOff')}
        </span>
      </label>

      {error ? (
        <p
          role="alert"
          style={{ color: 'var(--color-accent)', fontSize: 'var(--text-meta)', margin: 0 }}
        >
          {error}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <button
          type="submit"
          disabled={submitting}
          style={{
            ...primaryButtonStyle,
            opacity: submitting ? 0.6 : 1,
            cursor: submitting ? 'wait' : 'pointer',
          }}
        >
          {submitting
            ? t.t('dashboard.achievements.form.saving')
            : isEditMode
              ? t.t('dashboard.achievements.form.saveChanges')
              : t.t('dashboard.achievements.form.create')}
        </button>
        {isEditMode ? (
          <button
            type="button"
            onClick={onCancelEdit}
            disabled={submitting}
            style={ghostButtonStyle}
          >
            {t.t('dashboard.achievements.form.cancelEdit')}
          </button>
        ) : null}
      </div>
    </form>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────

function formFromAchievement(a: Achievement | null): FormState {
  if (!a) {
    return {
      name: '',
      icon: '🏅',
      description: '',
      criteriaKind: 'n_visits',
      threshold: '5',
      active: true,
    };
  }
  const criteria = a.criteria;
  const criteriaKind: AuthorableAchievementCriteriaKind =
    criteria.kind === 'first_wash' ||
    criteria.kind === 'n_visits' ||
    criteria.kind === 'spent_over' ||
    criteria.kind === 'streak_n_weeks'
      ? criteria.kind
      : 'n_visits';
  let threshold = '5';
  if (criteria.kind === 'n_visits' || criteria.kind === 'streak_n_weeks') {
    threshold = String(criteria.n);
  } else if (criteria.kind === 'spent_over') {
    threshold = String(criteria.amount / 100);
  }
  return {
    name: a.name['en'] ?? a.id,
    icon: a.icon || '🏅',
    description: a.description['en'] ?? '',
    criteriaKind,
    threshold,
    active: a.active,
  };
}

function describeCriteria(criteria: Achievement['criteria'], t: I18n): string {
  switch (criteria.kind) {
    case 'first_wash':
      return t.t('dashboard.achievements.row.criteria.firstWash');
    case 'n_visits':
      return t.t('dashboard.achievements.row.criteria.visits', { n: criteria.n });
    case 'spent_over':
      return t.t('dashboard.achievements.row.criteria.spend', {
        amount: money.format(criteria.amount),
      });
    case 'streak_n_weeks':
      return t.t('dashboard.achievements.row.criteria.streak', { n: criteria.n });
    case 'package_count':
    case 'add_on_count':
    case 'custom':
      return t.t('dashboard.achievements.row.criteria.manual');
  }
}

function Screen({ body }: { readonly body: string }) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <p style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>{body}</p>
    </main>
  );
}

// ── styles (mirror /loyalty/challenges) ──────────────────────────────────────

const disabledBannerStyle: React.CSSProperties = {
  marginBottom: 'var(--space-5)',
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid rgba(234,179,8,0.28)',
  background: 'rgba(234,179,8,0.08)',
};

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const fieldGrid: React.CSSProperties = { display: 'grid', gap: 'var(--space-2)', minWidth: 0 };

const helpTextStyle: React.CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const miniGhostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '6px 10px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};
